import { marketingHref } from "@/components/marketing/marketingLinks";
import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";
import type { ReactNode } from "react";

import { StyledAnchor } from "@/components/common/styles";
import { ArrowIcon } from "@/components/marketing/MarketingIcons";
import {
  ACCENT,
  FRAME,
  MID,
  NOT_WIDE,
  TINY,
  marketingPalette,
} from "@/components/marketing/tokens";
import type { Cta } from "@/content/types";

/**
 * `.container_new` and its five padding variants, which carry the whole page's
 * vertical rhythm, every section is one of these five and nothing else.
 *
 * `both` loses its max-width below 992 on the published site. That looks like a
 * mistake and is not one: the two sections that use it are the only full-bleed
 * blocks on a narrow screen, and reproducing the rhythm without it puts their
 * cards at a different width from their neighbours'.
 */
const PADDING = {
  top: { pt: "100px", [NOT_WIDE]: { pt: "64px" }, [TINY]: { pt: "48px" } },
  logos: { pt: "48px" },
  both: {
    py: "100px",
    [NOT_WIDE]: { py: "64px", maxWidth: "none" },
    [TINY]: { py: "48px" },
  },
  // Measured 48 at the wide steps and 64 only between 480 and 991: the
  // published `is-bottom` variant is the one that does not step to 100.
  bottom: { pb: "48px", [NOT_WIDE]: { pb: "64px" }, [TINY]: { pb: "48px" } },
  plain: {},
} satisfies Record<string, SxProps<Theme>>;

export function SectionContainer({
  padding,
  sx,
  children,
}: {
  padding: keyof typeof PADDING;
  sx?: SxProps<Theme>;
  children: ReactNode;
}) {
  return (
    <Box component="section" sx={{ position: "relative" }}>
      <Box
        sx={[
          {
            maxWidth: `${FRAME.header}px`,
            mx: "auto",
            px: "16px",
          },
          PADDING[padding],
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        {children}
      </Box>
    </Box>
  );
}

/** The hero pair is the only one that stretches once its column stacks. */
const BUTTON_WIDTH = {
  full: { width: 1 },
  hero: { width: "224px", [MID]: { width: 1 } },
  fixed: { width: "200px", [TINY]: { width: 1 } },
  hug: { width: "auto" },
};

/**
 * `.button_new`, the filled control, in the four widths the page uses.
 *
 * Every one of these is an anchor rather than a `RouteLink`, including the ones
 * that now land on this app's own routes. The query strings are the reason: an
 * express-checkout link is a pre-filled plan, `?start_google_login_flow` is a
 * valueless parameter the sign-in page keys off, and both would be re-serialised
 *, reordered, or normalised to `=true`, by a typed `search` object. An anchor
 * carries the string the site's analytics and checkout were built around.
 */
export function ActionButton({
  cta,
  width = "full",
  fill = "accent",
  arrow = true,
  sx,
}: {
  cta: Cta;
  width?: keyof typeof BUTTON_WIDTH;
  fill?: "accent" | "purple";
  /** The product cards' buttons publish their label alone. */
  arrow?: boolean;
  sx?: SxProps<Theme>;
}) {
  return (
    <StyledAnchor
      href={marketingHref(cta.href)}
      {...(cta.newTab ? { target: "_blank", rel: "noopener" } : {})}
      sx={[
        {
          ...BUTTON_WIDTH[width],
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          height: "48px",
          px: "16px",
          borderRadius: "8px",
          backgroundColor: fill === "purple" ? marketingPalette.purple : ACCENT,
          color: "common.white",
          fontSize: 16,
          lineHeight: "24px",
          transition: "padding-left 0.2s",
          "&:hover": {
            backgroundColor:
              fill === "purple"
                ? marketingPalette.purpleHover
                : marketingPalette.accentHover,
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {cta.label}
      {arrow ? <ArrowIcon /> : null}
    </StyledAnchor>
  );
}
